import { useLocation } from "react-router-dom";
import { Box, Typography, Button } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { VideoRoom } from "protogen/video_pb";
import { AccountStub, FamilyStub } from "protogen/common_pb";
import { Note } from "protogen/notes_pb";
import { Suggestions } from "protogen/suggestions_pb";
import { LocalUserChoices } from "@livekit/components-react";
import TwoPanelGrid, { Panel } from "components/details-page/TwoPanelGrid";
import useIsMobile from "components/hooks/useIsMobile";
import VideoCallDetailsPanel from "components/details-page/VideoCallDetailsPanel";
import VideoCallTranscriptAndComments from "./VideoCallTranscriptAndComments";
import Breadcrumbs from "components/common/Breadcrumbs";
import CopyButton from "components/common/CopyButton";

const mainHeader = (family: FamilyStub | undefined) => (
  <Box
    sx={{
      display: "flex",
      flexDirection: "row",
      alignItems: "center",
      maxWidth: "950px",
      width: "100%",
    }}
  >
    <Box>
      {family?.ref && (
        <Breadcrumbs
          breadcrumbs={[
            {
              name: family.name,
              link: `/families/${encodeURIComponent(family.ref)}`,
            },
          ]}
        />
      )}
      <Typography
        variant="h1Serif"
        sx={{
          margin: "4px 0",
        }}
      >
        Video call
      </Typography>
    </Box>
  </Box>
);

const activityHeader = (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "16px 0",
      gap: "12px",
      borderBottom: "1px solid #975d331a",
    }}
  >
    <Typography variant="h4">Activity</Typography>
  </Box>
);

const ToggleButton = (isCollapsed: boolean, onToggle: () => void) => (
  <Button
    onClick={onToggle}
    variant="outlined"
    size="small"
    startIcon={isCollapsed ? <ChevronLeft /> : <ChevronRight />}
    sx={{ whiteSpace: "nowrap", marginLeft: "8px" }}
  >
    {isCollapsed ? "Show activity" : "Hide activity"}
  </Button>
);

const headerAction = (videoRoom: VideoRoom | null) => (
  <Box display="flex" alignItems="center" gap={1}>
    <CopyButton
      value={`${window.location.origin}/video/${videoRoom?.uuid}`}
      variant="text"
      label="Copy invite link"
      sx={{ whiteSpace: "nowrap" }}
    />
  </Box>
);

export default ({
  videoRoom,
  participants,
  note,
  family,
  isCallActive,
  onReJoin,
  suggestions,
  onRefreshSuggestions,
  onVideoSetupJoin,
  token,
}: {
  videoRoom: VideoRoom | null;
  participants: AccountStub[];
  note: Note | null;
  family: FamilyStub | undefined;
  isCallActive: boolean;
  onReJoin: () => void;
  suggestions: Suggestions | null;
  onRefreshSuggestions: () => void;
  onVideoSetupJoin?: (choices: LocalUserChoices) => void;
  token?: string;
}) => {
  const isMobile = useIsMobile();
  const { hash } = useLocation();
  const defaultPanel = hash.match(/#details/) ? Panel.Right : Panel.Left;
  return (
    <>
      <TwoPanelGrid
        defaultPanel={defaultPanel}
        showHeaderAction={!isMobile}
        headerAction={headerAction(videoRoom)}
        leftPanelMaxWidth="100%"
        leftPanel={{
          title: mainHeader(family),
          switchContent: "Call details",
          content: (
            <VideoCallDetailsPanel
              note={note}
              isCallActive={isCallActive}
              onReJoin={onReJoin}
              videoRoom={videoRoom}
              suggestions={suggestions}
              onRefreshSuggestions={onRefreshSuggestions}
              familyRef={family?.ref || ""}
              onVideoSetupJoin={onVideoSetupJoin}
            />
          ),
          sx: { overflow: "hidden" },
        }}
        rightPanel={{
          title: isMobile ? mainHeader(family) : activityHeader,
          switchContent: "Activity",
          content: (
            <VideoCallTranscriptAndComments
              videoRoom={videoRoom}
              participants={participants}
              token={token}
            />
          ),
          collapsible: true,
          defaultCollapsed: true,
          customToggleButton: ToggleButton,
        }}
      />
    </>
  );
};
